import React, {ReactElement, useEffect, useState} from 'react';
import {
  Card,
  CardContent,
  createStyles,
  makeStyles,
  Theme,
  Typography
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {CARD_BOX_SHADOW} from 'src/style/variable';
import {useSnackbar} from 'notistack';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {getInsuranceCardImages} from '@shared/services/AppointmentSchedulingService';
import Loader from '@components/framework/loader/Loader';
import HelperUtils from '@shared/utils/HelperUtils';

const getImageUrls = (fileImage: any) => {
  let frontSideImageUrl = '',
    backSideImageUrl = '';
  if (fileImage.frontSide) {
    frontSideImageUrl = URL.createObjectURL(new Blob([fileImage.frontSide]));
  }
  if (fileImage.backSide) {
    backSideImageUrl = URL.createObjectURL(new Blob([fileImage.backSide]));
  }
  return {
    frontSideImageUrl,
    backSideImageUrl
  };
};

const InsuranceCardImagesStyles = makeStyles((theme: Theme) =>
  createStyles({
    cardRoot: {
      '& .MuiCardContent-root:last-child': {
        paddingBottom: '13px'
      },
      boxShadow: CARD_BOX_SHADOW,
      marginBottom: '1rem'
      // borderRadius: '1rem'
    },
    patientSection: {
      // maxWidth: '60rem'
    },
    addressValue: {
      display: 'flex',
      flexDirection: 'column',
      rowGap: '0.5rem'
    },
    title: {
      fontSize: '1.3rem',
      fontWeight: 600,
      paddingBlock: '1rem'
    },
    Content: {
      display: 'flex',
      gap: '1rem',
      flexDirection: 'column',
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row'
      }
    },
    uploadedAt: {
      marginTop: '1.5rem',
      textAlign: 'right'
    },
    ImagesParent: {
      flex: 1,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      alignItems: 'center'
    },
    Image1: {
      objectFit: 'contain',
      maxHeight: '20rem',
      flex: 1,
      width: '100%'
    },
    imagesViewTitle: {
      fontSize: '1.2rem',
      fontWeight: 600
    },
    loaderWrapper: {
      marginBottom: '1rem'
    }
  })
);

const InsuranceCardImage = ({
  clinicId,
  groupId,
  patientId,
  fileId1,
  fileId2,
  createdAt
}: {
  clinicId: number;
  groupId: number;
  patientId: number;
  fileId1: string;
  fileId2: string;
  createdAt?: string;
}): ReactElement => {
  const classes = InsuranceCardImagesStyles();
  const {enqueueSnackbar} = useSnackbar();
  const {t} = useTranslation();

  const [insuranceImage, setInsuranceImages] = useState<{
    frontSide: any;
    backSide: any;
    isLoading: boolean;
  }>({
    frontSide: null,
    backSide: null,
    isLoading: true
  });

  const imageUrls = getImageUrls(insuranceImage);

  const fetchTheInsuranceCardImages = async () => {
    try {
      const options = {
        clinic_id: clinicId
      };
      setInsuranceImages(p => ({
        ...p,
        isLoading: true
      }));
      const response1 = await getInsuranceCardImages(
        groupId,
        patientId,
        fileId1,
        options
      );
      const response2 = await getInsuranceCardImages(
        groupId,
        patientId,
        fileId2,
        options
      );
      setInsuranceImages(p => ({
        ...p,
        frontSide: response1,
        backSide: response2
      }));
    } catch (error: any) {
      enqueueSnackbar(
        error?.message
          ? error.message
          : t(
              `ELIGIBILITY_VERIFICATION.ERROR_IN_FETCHING_INSURANCE_CARD_IMAGES`
            ),
        SnackBarConfig('e')
      );
    } finally {
      setInsuranceImages(p => ({
        ...p,
        isLoading: false
      }));
    }
  };

  useEffect(() => {
    fetchTheInsuranceCardImages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, groupId, patientId, fileId1, fileId2]);

  return insuranceImage.isLoading ? (
    <div className={classes.loaderWrapper}>
      <Loader />
    </div>
  ) : (
    <Card className={`${classes.cardRoot} card`}>
      <CardContent>
        <div className={classes.Content}>
          {imageUrls.frontSideImageUrl && (
            <div className={classes.ImagesParent}>
              <div className={classes.imagesViewTitle}>
                {t('ELIGIBILITY_VERIFICATION.INSURANCE_CARD_IMAGES.FRONT_VIEW')}
              </div>
              <img
                src={imageUrls.frontSideImageUrl}
                className={classes.Image1}
              />
            </div>
          )}
          {imageUrls.backSideImageUrl && (
            <div className={classes.ImagesParent}>
              <div className={classes.imagesViewTitle}>
                {t('ELIGIBILITY_VERIFICATION.INSURANCE_CARD_IMAGES.REAR_VIEW')}
              </div>
              <img
                src={imageUrls.backSideImageUrl}
                className={classes.Image1}
              />
            </div>
          )}
        </div>
        {createdAt && (
          <p className={classes.uploadedAt}>
            {t('ELIGIBILITY_VERIFICATION.INSURANCE_CARD_IMAGES.UPLOADED_AT')}:{' '}
            {HelperUtils.formatDisplayDateTime(createdAt)}
          </p>
        )}
      </CardContent>
    </Card>
  );
};
// clinicId, groupId,patientId, insurances
const InsuranceCardImages = ({
  clinicId,
  groupId,
  patientId,
  insurances,
  showTitle = true
}: {
  clinicId: number;
  groupId: number;
  patientId: number;
  insurances: any[];
  showTitle?: boolean;
}): ReactElement => {
  const classes = InsuranceCardImagesStyles();
  const {t} = useTranslation();

  return (
    <div className={`section-header ${classes.patientSection}`}>
      {showTitle && (
        <Typography className={classes.title}>
          {t('ELIGIBILITY_VERIFICATION.INSURANCE_CARD')}
        </Typography>
      )}
      {insurances.map((insurance: any, idx) => (
        <React.Fragment key={idx}>
          <InsuranceCardImage
            clinicId={clinicId}
            groupId={groupId}
            patientId={patientId}
            fileId1={insurance?.['file_id1']}
            fileId2={insurance?.['file_id2']}
            createdAt={insurance?.['created_at']}
          />
        </React.Fragment>
      ))}
    </div>
  );
};

export default InsuranceCardImages;
